// campaign-content-edit-modal.tsx — #103 tier 2: edit a LAUNCHED campaign's
// message text and attachment, never its audience or timing (those were fixed
// at launch — Redis claims are seeded from the contact count, so changing them
// mid-run desyncs the schedule).
//
// WHY EDITING WORKS AT ALL: a campaign does not mint all its messages up front.
// Sends are staggered ~7s per contact and each recipient's message is created
// at its own due time from `campaign.template`, which the backend re-reads on
// EVERY dispatch. Patching the template mid-run therefore reaches every
// recipient not yet minted — and recipients already minted keep the OLD body.
// That mixed audience is disclosed, not glossed (see MIXED_AUDIENCE_LINE).
//
// THIS COMPONENT ORCHESTRATES NOTHING. It makes three server calls:
//
//   open           -> campaign-edit-begin   { campaignId }
//   Save           -> campaign-edit-commit  { campaignId, template, editSessionId }
//   Cancel/unmount -> campaign-edit-abort   { campaignId, editSessionId }
//   Resume sending -> campaign-edit-abort   { campaignId, recover: true }
//
// Each is ONE server call that cannot be half-applied and returns a definitive
// outcome this file simply renders. The pause, the drip pause, the template
// write, the drip-step mirror, the resume and the NON-idempotent `resumeDrip`
// catch-up all happen server-side, in order, awaited (backend
// `campaign-edit-actions.ts`).
//
// AN EARLIER VERSION OF THIS FILE DID ALL OF THAT ITSELF and could double-send
// an entire audience: a client-side timeout rejected without aborting the
// in-flight request, so a second `resumeDrip` overlapped the first, and
// `advanceDrip` underneath it CREATES the next step's campaign over the same
// audience. Do not reintroduce refs, timeouts, retries or ordering devices
// here — every one of them is a way for the client to disagree with the server
// about what happened.
//
// THE TWO RULES THE CONTRACT DEPENDS ON:
//   1. `editSessionId` from begin is sent on commit AND on the cancel/unmount
//      abort. Without it the template is still saved but NOTHING resumes.
//   2. `recover: true` is sent ONLY from the explicit "Resume sending" button.
//      An abort that merely lost its session id is a different intent, and
//      treating it as recovery lets it resume another live editor's pause.
//
// WHY "Resume sending" IS LOAD-BEARING, NOT POLISH: making recovery explicit
// means an abort that lost its session STRANDS the pause instead of resuming
// it. Nothing else in the product can resume a paused campaign — standalone
// campaigns have no pause/resume menu item at all — so without this button a
// stranded campaign is unrecoverable. Reopening this editor on such a campaign
// is the recovery entry point (begin reports `pausedForEdit && !ownsPause`).
//
// EDITOR REUSE: the message+attachment surface is the SAME StepEditor /
// DripMessageEditor pair DripStepEditModal uses, bridged through the shared
// DripEditorReducer working-copy slice. Here that slice is a scratch buffer
// only — the template is sent to the server by `campaign-edit-commit`, never
// persisted through DBDripThunks.
import React from 'react';
import { namespace } from '@common/config/constants';
import { useTranslation } from 'react-i18next';
import { X, PauseCircle, Check, PlayCircle } from 'lucide-react';

import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import {
  DBCampaignThunks,
  DBMessageLogThunks,
  campaignsSelectOneObjectByQuery,
} from '@common-reducers/DBServiceThunks';
import {
  useAbortCampaignEditMutation,
  useBeginCampaignEditMutation,
  useCommitCampaignEditMutation,
} from '@common/reducers/backend-api/backend-api';
import { selectCampaignProgressStats } from '@common-reducers/CampaignSelectors';
import { computeCampaignProgress } from '@common/services/campaign-progress';
import {
  hydrate,
  reset,
  nextBridgeId,
  type DripStepTemplate,
} from '@common-reducers/DripEditorReducer';
import type { Campaign } from '@common/models/campaign-model';
import type { CampaignEditBeginResult, CampaignEditFinishResult } from '@common-models/campaign-edit';

import { StepEditor } from './drip-message-editor';
import { CampaignEditorDialog } from './campaign-editor-dialog';
import { beginBanner, finishBanner, type EditBanner, type EditFinishOperation } from './campaign-edit-copy';
import { mergeEditedTemplate } from './campaign-template-merge';

interface CampaignContentEditModalProps {
  campaignId: string;
  onClose: () => void;
}

/** `opening` — begin in flight. `editing` — the form. `saving`/`recovering` —
 *  a server call in flight. `finished` — a commit or a recovery came back and
 *  the banner reports it. `unavailable` — begin itself refused/failed, so there
 *  is no edit session, nothing to save and nothing to abort. There is no
 *  client-side "resumed?" state anywhere: the server answers that. */
type Phase = 'opening' | 'editing' | 'saving' | 'recovering' | 'finished' | 'unavailable';

// Four visually DISTINCT palettes, from the campaigns-redesign token set
// (src/styles/campaigns-redesign.css) — the tone the copy module computes has to
// be legible on screen or the distinction it draws is dead code.
//   info    = expected and under control (paused while you edit, resumes on save)
//   warning = amber: something will NOT resume
//   error   = red: your changes did not land
// NOTE `--danger` is not defined in the `.campaigns-rd` scope at all, so every
// `var(--danger, #d33)` in this folder silently renders the hard-coded fallback
// and ignores the theme (including dark mode). This file used it twice and now
// uses the real `--escalate` triple in both places — the banner tone here and
// the footer error text below. `wizard.tsx:1263` still has the stale form; it is
// outside this change.
// Exported for `scheduled-campaign-edit-modal.tsx` (#103 tier 1), which renders
// the same `EditBanner` shape from the same copy module. One tone→palette map,
// so the two editors cannot disagree about what "this did not save" looks like.
export const TONE_STYLES: Record<EditBanner['tone'], { bg: string; line: string; fg: string }> = {
  info: { bg: 'var(--bt-blue-soft)', line: 'var(--bt-blue-line)', fg: 'var(--bt-blue-deep)' },
  warning: { bg: 'var(--kbmiss-soft)', line: 'var(--kbmiss-line)', fg: 'var(--kbmiss)' },
  error: { bg: 'var(--escalate-soft)', line: 'var(--escalate-line)', fg: 'var(--escalate)' },
  success: { bg: 'var(--success-soft)', line: 'var(--success-line)', fg: 'var(--success)' },
};

export const CampaignContentEditModal: React.FC<CampaignContentEditModalProps> = ({ campaignId, onClose }) => {
  const { t } = useTranslation(namespace);
  const dispatch = useAppDispatch();
  const [beginCampaignEdit] = useBeginCampaignEditMutation();
  const [commitCampaignEdit] = useCommitCampaignEditMutation();
  const [abortCampaignEdit] = useAbortCampaignEditMutation();

  const [phase, setPhase] = React.useState<Phase>('opening');
  const [begin, setBegin] = React.useState<CampaignEditBeginResult | null>(null);
  const [finish, setFinish] = React.useState<
    { result: CampaignEditFinishResult; operation: EditFinishOperation; templateSavedEarlier?: boolean } | null
  >(null);
  /** Last failed CALL (network/HTTP), not a failed outcome — an outcome always
   *  comes back as booleans and is rendered by the banner. */
  const [callError, setCallError] = React.useState<string | null>(null);

  // Two refs, and NEITHER sequences anything — no ordering device, no retry
  // latch, no timeout survives from the old client-orchestrated version.
  //
  // `beginPromiseRef` is THE edit session, in the only form that is always
  // available: a promise of the session id. It is one value carrier doing three
  // jobs that all need the same thing —
  //   - the unmount cleanup cannot read state, and the session id may not exist
  //     yet (see the cleanup effect: this is what stops a close during the begin
  //     round trip from stranding the pause);
  //   - `save()` needs the id to authorise the resume;
  //   - it is set synchronously, so it doubles as the "already begun" latch. A
  //     second `begin` from a double-invoked effect would take a session that
  //     owns NOTHING (the first already paused the campaign) and overwriting the
  //     first id would mean nothing ever resumes.
  //
  // DOUBLE-INVOKE IS ONLY HALF-COVERED, and deliberately so: `begin` is latched,
  // the TEARDOWN is not. Under React StrictMode's mount/unmount/mount, the first
  // teardown would chain a real abort against an editor that is about to be
  // re-mounted, and the re-mount would not re-begin (the latch holds) — leaving
  // the banner claiming a pause that was just undone. Nothing in this repo wraps
  // this tree in StrictMode, and the only clean fix is a defer-and-recheck, i.e.
  // exactly the ordering device this task exists to remove. Left as a documented
  // residual. A `key={campaignId}` does NOT mitigate it — StrictMode's double
  // invoke reuses the same fiber, so the refs survive; the key addresses a
  // DIFFERENT hazard, below.
  //
  // BECAUSE THE LATCH IS NOT KEYED ON `campaignId`, this component must never be
  // handed a different campaign while mounted: the second campaign would skip
  // `begin` entirely (never paused, no session id) and render the first one's
  // state. Both call sites (`sequence-detail.tsx`, `management-list.tsx`)
  // therefore render it as `key={campaignId}`, which makes a campaign change a
  // remount instead of a prop swap. Keep that key if you add another call site.
  //
  // It resolves to `undefined` when begin failed — never rejects, so no consumer
  // has to handle a rejection.
  const beginPromiseRef = React.useRef<Promise<string | undefined> | null>(null);
  /** A commit or a recovery already resumed (or definitively reported) this
   *  edit — the unmount abort would be a pointless extra request. */
  const finishedRef = React.useRef(false);

  React.useEffect(() => {
    dispatch(DBCampaignThunks.find({ _id: campaignId }));
    // Populate message logs so the already-sent disclosure below has data.
    //
    // KNOWN LIMITATION (documented, not fixed): this is unpaginated
    // ($limit:-1) but `DBMessageLogReducer` caps at `maxEntities: 10000` with
    // oldest-first eviction, so on a >10k-recipient campaign the "N of M" line
    // can under-report N. A correct fix needs a server-side count aggregate.
    dispatch(DBMessageLogThunks.find({ campaignId, $limit: -1 }));
  }, [dispatch, campaignId]);

  const campaign = useAppSelector((s) =>
    campaignsSelectOneObjectByQuery(s.DBCampaignReducer, { _id: campaignId }),
  ) as Campaign | undefined;

  // ── begin: pause for editing ─────────────────────────────────────────────
  React.useEffect(() => {
    if (beginPromiseRef.current) return;
    // Assigned synchronously, before any await, so a double-invoked effect sees
    // it and cannot take a second session.
    beginPromiseRef.current = beginCampaignEdit({ campaignId }).unwrap().then(
      (result) => {
        setBegin(result);
        setPhase('editing');
        return result.editSessionId;
      },
      () => {
        // begin refuses a terminal campaign (BadRequest) and fails on a network
        // error. Either way there is no edit session: nothing to abort on
        // unmount, and `campaign-edit-commit` guards its write on
        // `campaignStatus: 'paused'` so a save could not land either. Say so
        // instead of offering a form that will refuse.
        setPhase('unavailable');
        return undefined;
      },
    );
  }, [beginCampaignEdit, campaignId]);

  // ── abort on cancel / unmount ────────────────────────────────────────────
  // NEVER with `recover: true` — see the header.
  //
  // Chained onto begin's own promise rather than reading an id that may not
  // exist yet. Cancel, the X and the backdrop are all live DURING the begin
  // round trip, so a close inside that window would otherwise unmount before
  // there is any session to abort with — begin then lands afterwards and leaves
  // the campaign paused with a marker nobody holds. There is no server-side
  // sweeper by decision, and for a standalone campaign the only remedy in the
  // product is re-opening this modal and clicking Resume sending. One mis-click
  // would produce exactly the invisible-stop #103 exists to remove.
  //
  // This is a continuation, not a sequencing device: it adds no ordering
  // between server calls (there is only ever one in flight here), no retry and
  // no timeout. The abort is fire-and-forget — the component is gone, and the
  // server action is atomic and idempotent on its own.
  React.useEffect(() => {
    return () => {
      if (finishedRef.current) return;
      const pendingSession = beginPromiseRef.current;
      if (!pendingSession) return;
      void pendingSession.then((editSessionId) => {
        if (!editSessionId) return;
        void abortCampaignEdit({ campaignId, editSessionId });
      });
    };
  }, [abortCampaignEdit, campaignId]);

  // ── Editor working copy (DripEditorReducer is a scratch buffer here) ──────
  const hydratedByUsRef = React.useRef(false);
  const hydratedForThisCampaign = useAppSelector(
    (s) => s.DripEditorReducer.hydrated && s.DripEditorReducer.dripId === campaignId,
  );

  React.useEffect(() => {
    if (!campaign || hydratedForThisCampaign) return;
    const bridgeId = nextBridgeId();
    const template: DripStepTemplate = { ...(campaign.template ?? {}), _id: bridgeId };
    hydratedByUsRef.current = true;
    dispatch(hydrate({
      dripId: campaignId,
      steps: [{ stepIndex: 0, template, gapAfter: null }],
      name: campaign.name ?? '',
      stopOnReply: true,
      timing: { launch: 'now' },
    }));
    // hydrate once per opened campaign; keystrokes flow into the slice via
    // StepEditor's own mirror effect, not back through here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, campaign, campaignId, hydratedForThisCampaign]);

  // Reset the shared working-copy slice on unmount — but ONLY if THIS modal
  // hydrated it: DripEditorReducer is a store-wide singleton that deliberately
  // survives unmount after a launch, so a fast unmount here must not clobber
  // another consumer's working copy.
  React.useEffect(() => {
    return () => { if (hydratedByUsRef.current) dispatch(reset()); };
  }, [dispatch]);

  const sliceSteps = useAppSelector((s) => s.DripEditorReducer.steps);

  // ── the three calls ──────────────────────────────────────────────────────
  const save = async () => {
    // Never save off a slice this modal didn't hydrate — DripEditorReducer is
    // a store-wide singleton, so before this campaign's doc loads `sliceSteps[0]`
    // can belong to a different drip/campaign entirely.
    if (!hydratedForThisCampaign) return;
    const working = sliceSteps[0]?.template;
    if (!working) return;

    setCallError(null);
    setPhase('saving');
    // MERGE over the stored template, never replace it: the commit is a
    // whole-field patch and this editor models only nine fields, so sending the
    // working copy alone would silently DELETE `buttons` and every other
    // inherited field from a live campaign. See campaign-template-merge.ts.
    const template = mergeEditedTemplate(campaign?.template, working);
    try {
      // Already resolved in practice — Save is only enabled once begin answered
      // — but reading the session from the same promise keeps ONE carrier for it.
      const editSessionId = await (beginPromiseRef.current ?? Promise.resolve(undefined));
      const result = await commitCampaignEdit({
        campaignId,
        template,
        editSessionId,
      }).unwrap();
      finishedRef.current = true;
      setFinish({ result, operation: 'commit' });
      setPhase('finished');
    } catch {
      // The call itself failed, so nothing was applied — back to the form.
      setCallError("Couldn't save your changes — try again.");
      setPhase('editing');
    }
  };

  const resumeSending = async () => {
    setCallError(null);
    setPhase('recovering');
    try {
      const result = await abortCampaignEdit({ campaignId, recover: true }).unwrap();
      finishedRef.current = true;
      // Carry forward whether a commit ALREADY saved the template. The abort
      // result always says `templateSaved: false` (it never saves), so without
      // this the banner would tell a user who just saw "Message updated." that
      // their changes were not saved, and would drop a mixed-audience
      // disclosure that is still true. Functional update so the value is read
      // at apply time, not from this closure.
      setFinish((prev) => ({
        result,
        operation: 'recover',
        templateSavedEarlier: prev?.result.templateSaved || prev?.templateSavedEarlier || false,
      }));
      setPhase('finished');
    } catch {
      setCallError("Couldn't resume sending — try again.");
      setPhase(finish ? 'finished' : 'editing');
    }
  };

  const busy = phase === 'saving' || phase === 'recovering';

  const cancel = () => {
    // Ignore backdrop/X clicks while a call is in flight — the unmount cleanup
    // performs the abort, and interrupting a commit gains nothing.
    if (busy) return;
    onClose();
  };

  // ── disclosure + banner ──────────────────────────────────────────────────
  const progressStats = useAppSelector((s) => selectCampaignProgressStats(s, campaignId));
  const totalContacts = campaign?.contactCount ?? 0;
  // N comes from LIVE delivery progress (the same selector + math
  // sequence-detail.tsx's StepMeter uses for "reached WhatsApp"), not from a
  // terminal-only rule — this modal only ever opens on running/active/paused.
  const { totalProcessed, totalFailed } = computeCampaignProgress(progressStats, totalContacts);
  const alreadySent = Math.max(0, totalProcessed - totalFailed);

  // Every sentence is derived from the server's booleans; `warnings[]` is
  // appended verbatim. See campaign-edit-copy.ts for why rendering warnings
  // alone would lie about a paused sequence.
  const banner: EditBanner | null = finish
    ? finishBanner(finish.result, {
      operation: finish.operation,
      pauseWasEditOwned: Boolean(begin?.pausedForEdit),
      templateSavedEarlier: finish.templateSavedEarlier,
    }, t)
    : beginBanner(begin, t);
  const tone = TONE_STYLES[banner?.tone ?? 'info'];
  const isFinished = phase === 'finished';
  // `campaign-edit-commit` guards the template write on `campaignStatus:
  // 'paused'`, so a save is only ever possible on a campaign begin actually
  // paused. Anything else would round-trip just to be refused.
  const canSave = Boolean(begin?.paused) && hydratedForThisCampaign;
  // The side preview reads the draft the mounted editor mirrors — show it only
  // while the editor is on screen.
  const editorShown = !isFinished && phase !== 'unavailable' && phase !== 'opening' && hydratedForThisCampaign;
  const title = t('campaignsRedesign.editModal.title', 'Edit message');

  return (
    // The schedule-message editor's frame (editor card + WhatsApp preview
    // panel), so the editor's Templates / Save as template dialogs open on top
    // of it rather than behind — see campaign-editor-dialog.tsx.
    <CampaignEditorDialog onClose={cancel} title={title} showPreview={editorShown}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid var(--line)', background: 'var(--surface)' }}>
          <strong style={{ fontSize: 14, letterSpacing: '-0.01em' }}>{title}</strong>
          <button type="button" className="btn ghost icon sm" onClick={cancel} aria-label={t('common.close', 'Close')}><X size={16} /></button>
        </div>

        {/* Persistent and non-dismissable: a campaign that silently stops
            sending is exactly the invisible-failure class #103 exists to
            remove. Before Save it discloses the pause; after Save/Resume it
            reports what the SERVER did, one sentence per fact, plus the
            server's own warnings verbatim. */}
        {banner && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 16px', background: tone.bg, borderBottom: `1px solid ${tone.line}` }}>
            {banner.tone === 'success' ? <Check size={15} stroke={tone.fg} style={{ flexShrink: 0, marginTop: 1 }} /> : <PauseCircle size={15} stroke={tone.fg} style={{ flexShrink: 0, marginTop: 1 }} />}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {banner.lines.map((text) => (
                <span key={text} style={{ fontSize: 12.5, color: tone.fg }}>{text}</span>
              ))}
              {banner.warnings.map((text) => (
                <span key={text} style={{ fontSize: 12.5, color: tone.fg }}>{text}</span>
              ))}
            </div>
          </div>
        )}

        {phase === 'unavailable' && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 16px', background: 'var(--kbmiss-soft)', borderBottom: '1px solid var(--kbmiss-line)' }}>
            <PauseCircle size={15} stroke="var(--kbmiss)" style={{ flexShrink: 0, marginTop: 1 }} />
            <span style={{ fontSize: 12.5, color: 'var(--kbmiss)' }}>
              {t('campaignsRedesign.editModal.unavailable', "This campaign can't be edited right now — it may have finished sending. Nothing was changed.")}
            </span>
          </div>
        )}

        {!isFinished && phase !== 'unavailable' && totalContacts > 0 && (
          <div style={{ padding: '8px 16px', fontSize: 12, color: 'var(--muted)', borderBottom: '1px solid var(--line)', background: 'var(--surface)' }}>
            {t(
              'campaignsRedesign.editModal.alreadyReceived',
              '{{count}} of {{total}} already received the previous version.',
              { count: alreadySent, total: totalContacts },
            )}
          </div>
        )}

        <div style={{ flex: 1, overflow: 'auto', minHeight: 0, padding: 16 }}>
          {isFinished || phase === 'unavailable' ? (
            <div style={{ padding: 24, color: 'var(--muted)', fontSize: 13 }}>{t('campaignsRedesign.editModal.canClose', 'You can close this window.')}</div>
          ) : phase === 'opening' ? (
            <div style={{ padding: 24, color: 'var(--muted)', fontSize: 13 }}>{t('campaignsRedesign.editModal.pausing', 'Pausing the campaign…')}</div>
          ) : hydratedForThisCampaign ? (
            <StepEditor idx={0} showPreview={false} />
          ) : (
            <div style={{ padding: 24, color: 'var(--muted)', fontSize: 13 }}>{t('campaignsRedesign.editModal.loadingEditor', 'Loading editor…')}</div>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, padding: '12px 16px', borderTop: '1px solid var(--line)', background: 'var(--surface)' }}>
          {callError && (
            <span style={{ fontSize: 12, color: 'var(--escalate)', marginInlineEnd: 'auto' }}>{callError}</span>
          )}
          {/* The ONLY control in the product that can resume a paused campaign.
              Offered exactly where it can work: a pause carrying an edit-session
              marker (see canRecoverCampaignPause). */}
          {banner?.showResumeSending && (
            <button type="button" className="btn ghost" onClick={() => { void resumeSending(); }} disabled={busy}>
              <PlayCircle size={14} style={{ marginInlineEnd: 6, verticalAlign: 'text-bottom' }} />
              {/* While the form is on screen this button DISCARDS whatever was
                  typed (recovery resumes, it never saves) — and it is offered
                  exactly where the user is most likely to have typed. The label
                  says so before the click; the outcome banner says so after. */}
              {phase === 'recovering'
                ? t('campaignsRedesign.editModal.resuming', 'Resuming…')
                : isFinished
                  ? t('campaignsRedesign.editModal.resumeSending', 'Resume sending')
                  : t('campaignsRedesign.editModal.resumeWithoutSaving', 'Resume sending without saving')}
            </button>
          )}
          {isFinished || phase === 'unavailable' ? (
            <button type="button" className="btn primary" onClick={onClose}>{t('common.done', 'Done')}</button>
          ) : (
            <>
              <button type="button" className="btn ghost" onClick={cancel} disabled={busy}>{t('common.cancel', 'Cancel')}</button>
              <button
                type="button"
                className="btn primary"
                onClick={() => { void save(); }}
                disabled={!canSave || busy}
              >
                {phase === 'saving' ? t('campaignsRedesign.editModal.saving', 'Saving…') : t('common.saveChanges', 'Save changes')}
              </button>
            </>
          )}
        </div>
    </CampaignEditorDialog>
  );
};
